// The operator overrides as REST twins (plan 101 rows 17-1 / 17-16): set an
// account's tier / seats / spend cap, decide an OSS-verification application.
//
//   PUT  /v1/admin/accounts/:id/tier             { tier, confirm }
//   PUT  /v1/admin/accounts/:id/seats            { seats, confirm }
//   PUT  /v1/admin/accounts/:id/spend-cap        { spend_cap_usd, confirm }
//   POST /v1/admin/oss-verifications/:id/decide  { decision, note?, confirm }
//
// They used to ride the MCP `platform` verb over `POST /v1/mcp` (`lib/adminMcp.ts`,
// deleted): a second transport for one control, refused in prod with no operator
// token. Every body is strict and REQUIRES `confirm: true`. The CALLER passes it:
// it is the human's confirm click (`createConfirmGate`), written at the call
// site that runs after that click, never defaulted here.
//
// Scope `admin:accounts`, operator-credential-only (#5724), so each write asks
// `withOperatorCredential` first; the door's two refusals come back as their own
// outcome kinds and no write is sent. Past the door, a refusal (400 / 403 / 404,
// a 5xx, a dead network) folds into a non-throwing `{ ok: false, text }` with the
// API's own `detail` (`errorReason`), so the caller shows it inline. A 200 is
// what the server did, not what the account now looks like: approving also
// re-tiers the account to `oss_verified`, so the caller re-reads.

import type { OssVerificationDecision, Tier } from '@developerz/domain';
import { t } from '../i18n';
import { errorReason } from './api';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import { SESSION_TOKEN } from './session';

/**
 * One operator override. `spend_cap_usd: null` resets to the default (a `0` is
 * refused: the bound is `> 0`).
 */
export type OverrideWrite = { readonly confirm: true } & (
  | { readonly action: 'set_tier'; readonly account_id: string; readonly tier: Tier }
  | { readonly action: 'set_seats'; readonly account_id: string; readonly seats: number }
  | {
      readonly action: 'set_spend_cap';
      readonly account_id: string;
      readonly spend_cap_usd: number | null;
    }
  | {
      readonly action: 'oss_verify_decide';
      readonly request_id: string;
      readonly decision: OssVerificationDecision;
      readonly note?: string;
    }
);

/** An override's outcome past the door: it landed, or the API's reason it did not. */
export type OverrideResult = { readonly ok: true } | { readonly ok: false; readonly text: string };

type Request = { readonly method: 'PUT' | 'POST'; readonly path: string; readonly body: object };

const account = (id: string, leaf: string): string =>
  `/v1/admin/accounts/${encodeURIComponent(id)}/${leaf}`;

/** The route and strict body for one override. */
function request(w: OverrideWrite): Request {
  switch (w.action) {
    case 'set_tier':
      return {
        method: 'PUT',
        path: account(w.account_id, 'tier'),
        body: { tier: w.tier, confirm: w.confirm },
      };
    case 'set_seats':
      return {
        method: 'PUT',
        path: account(w.account_id, 'seats'),
        body: { seats: w.seats, confirm: w.confirm },
      };
    case 'set_spend_cap':
      return {
        method: 'PUT',
        path: account(w.account_id, 'spend-cap'),
        body: { spend_cap_usd: w.spend_cap_usd, confirm: w.confirm },
      };
    case 'oss_verify_decide':
      return {
        method: 'POST',
        path: `/v1/admin/oss-verifications/${encodeURIComponent(w.request_id)}/decide`,
        body: {
          decision: w.decision,
          ...(w.note !== undefined ? { note: w.note } : {}),
          confirm: w.confirm,
        },
      };
  }
}

/**
 * Send one confirmed override on an `admin:accounts` operator credential.
 * Never throws: the write is sent raw so a 401 on the credential reaches
 * `withOperatorCredential`'s one retry, and only what is left over folds into
 * `{ ok: false }`.
 */
export async function sendOverride(
  write: OverrideWrite,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<OverrideResult>> {
  const req = request(write);
  try {
    return await withOperatorCredential(
      { scopes: ['admin:accounts'], name: `admin console: ${write.action}`, token },
      async (send): Promise<OverrideResult> => {
        await send<unknown>(req.path, { method: req.method, body: JSON.stringify(req.body) });
        return { ok: true };
      },
    );
  } catch (e) {
    return { kind: 'ok', value: { ok: false, text: errorReason(e, t('admin.write_error')) } };
  }
}
